<template>
  <BaseModal :modalTitle="$t('area.drop-point.create.title')">
    <UiBaseFormVee
      :onSubmit="onCreate"
      :errorText="errorText">
      <div class="mb-6">
        <UiInputTextFieldVee
          name="name"
          :label="$t('area.drop-point.field.name')"
          :rules="['required']"
          isRequired
          showAsRequired
          :unit="currentLocale.toUpperCase()"
          v-model="form.name" />
        <UiInputTextFieldVee
          name="address"
          :label="$t('form.label.address')"
          :rules="['required']"
          isRequired
          showAsRequired
          v-model="form.address">
          <template v-if="isMagicAddressEnabled" #trailingIcon>
            <MagicAddressButton
              :currentAddressLine="form.address"
              @on-fill="onMagicAddress" />
          </template>
        </UiInputTextFieldVee>
        <div class="grid grid-cols-2 gap-4">
          <UiInputTextFieldVee
            name="postalCode"
            :label="$t('form.label.postal-code')"
            :rules="['required']"
            isRequired
            showAsRequired
            v-model="form.postalCode" />
          <UiInputTextFieldVee
            name="city"
            :label="$t('form.label.city')"
            :rules="['required']"
            isRequired
            showAsRequired
            v-model="form.city" />
        </div>
        <UiInputTextFieldVee
          name="placement"
          :label="$t('area.drop-point.field.placement-instructions')"
          :placeholder="$t('area.drop-point.placeholder.placement')"
          :rules="['required']"
          isRequired
          showAsRequired
          :unit="currentLocale.toUpperCase()"
          v-model="form.placement" />
        <UiInputPhoneVee
          name="phone"
          :label="$t('form.label.phone')"
          :rules="['requiredPhone', 'phone']"
          isRequired
          showAsRequired
          v-model="form.phone" />
      </div>
      <div class="mb-2">
        <span class="mb-3 block text-sm font-medium text-text-primary">
          {{ $t('area.drop-point.field.opening-hours') }}
        </span>
        <div class="flex flex-col gap-2">
          <div
            v-for="day in form.openingHours"
            :key="day.key"
            class="flex flex-col gap-1">
            <div class="flex w-fit items-center gap-2">
              <AppSwitch
                :isActive="day.isOpen"
                :title="day.label"
                @on-toggle="day.isOpen = !day.isOpen" />
              <span class="text-sm text-text-primary">{{ day.label }}</span>
              <span
                v-if="!day.isOpen"
                class="text-sm text-text-secondary">
                – {{ $t('area.drop-point.state.closed') }}
              </span>
            </div>
            <div
              v-if="day.isOpen"
              class="flex items-end gap-3">
              <UiInputSelectVee
                :name="`${day.key}Open`"
                :label="$t('area.drop-point.field.opens')"
                :options="halfHourOptions"
                class="w-32"
                v-model="day.openTime" />
              <UiInputSelectVee
                :name="`${day.key}Close`"
                :label="$t('area.drop-point.field.closes')"
                :options="halfHourOptions"
                class="w-32"
                v-model="day.closeTime" />
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="activeWalkInTats.length > 0"
        class="mb-6">
        <span class="mb-3 block text-sm font-medium text-text-primary">
          {{ $t('area.drop-point.field.walk-in-options') }}
        </span>
        <div class="flex flex-col gap-2">
          <label
            v-for="tat in activeWalkInTats"
            :key="tat.id"
            class="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              class="accent-primary h-4 w-4 rounded border-border-subtle"
              :value="tat.id"
              v-model="form.walkInTatIDs" />
            <span class="text-sm text-text-primary">
              {{ tat.getName(currentLocale) }}
            </span>
          </label>
        </div>

        <div
          v-if="form.walkInTatIDs.length > 0"
          class="mt-4 flex flex-col gap-1">
          <span class="text-sm text-text-primary">
            {{ $t('W: Walk-in tidsvindue') }}
          </span>
          <div class="flex items-end gap-3">
            <UiInputSelectVee
              name="walkInWindowStart"
              :label="$t('W: Start')"
              :unselectedText="$t('W: Vælg...')"
              :options="walkInStartOptions"
              :rules="['required']"
              isRequired
              showAsRequired
              class="w-32"
              v-model="form.walkInWindowStart" />
            <UiInputSelectVee
              name="walkInWindowEnd"
              :label="$t('W: Slut')"
              :unselectedText="$t('W: Vælg...')"
              :options="walkInEndOptions"
              :rules="['required']"
              isRequired
              showAsRequired
              class="w-32"
              v-model="form.walkInWindowEnd" />
          </div>
        </div>
      </div>
      <div class="mb-6">
        <span class="mb-3 block text-sm font-medium text-text-primary">
          {{ $t('W: Selvsendelse (kunde-app)') }}
        </span>
        <div class="flex items-center gap-3">
          <AppSwitch
            :isActive="form.isSelfSendTarget"
            :title="$t('W: Brug til selvsendelse i kunde-app')"
            @on-toggle="form.isSelfSendTarget = !form.isSelfSendTarget" />
          <span class="text-sm font-medium text-text-primary">
            {{ $t('W: Brug til selvsendelse i kunde-app') }}
          </span>
        </div>
        <p class="mt-1 text-sm text-text-secondary">
          {{ $t('W: Kunder, der vælger "Jeg sender selv", får at vide, at de skal sende deres prøver til dette droppesteds adresse. Kun ét droppested kan bruges til dette ad gangen – slås det til her, slås det fra på det tidligere.') }}
        </p>
      </div>
      <div class="mb-6">
        <span class="mb-3 block text-sm font-medium text-text-primary">
          {{ $t('term.status') }}
        </span>
        <div class="flex items-center gap-3">
          <AppSwitch
            :isActive="form.isActive"
            :title="$t('state.active')"
            @on-toggle="form.isActive = !form.isActive" />
          <span class="text-sm font-medium text-text-primary">
            {{ $t('state.active') }}
          </span>
        </div>
      </div>
      <template #buttons>
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { canUseMagicAddress } from '@/dev/magicAddress'
import type { IMagicAddress } from '@/dev/magicAddress'
import type { IDropPointLab } from '@team-milva/lab-models/resources'
import { useDropPointStore } from '@/stores/dropPointStore'
import { useModalStore } from '@/stores/modalStore'
import {
  useTurnaroundTimeStore,
  TurnaroundTime,
} from '@/stores/turnaroundTimeStore'
import { useAppStore } from '@/stores/appStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { mapActions, mapState } from 'pinia'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'CreateDropPointModal',

  data() {
    return {
      isMagicAddressEnabled: canUseMagicAddress(),
      unsavedGuard: useUnsavedChangesGuard(),
      isLoading: false,
      errorText: '',
      form: {
        name: '',
        address: '',
        postalCode: '',
        city: '',
        placement: '',
        phone: { number: 0, iso2: 'dk' },
        isActive: true,
        isSelfSendTarget: false,
        walkInTatIDs: [] as string[],
        walkInWindowStart: '',
        walkInWindowEnd: '',
        openingHours: [
          {
            key: 'monday' as const,
            label: 'Mandag',
            weekday: 1 as const,
            isOpen: true,
            openTime: '08:00',
            closeTime: '17:00',
          },
          {
            key: 'tuesday' as const,
            label: 'Tirsdag',
            weekday: 2 as const,
            isOpen: true,
            openTime: '08:00',
            closeTime: '17:00',
          },
          {
            key: 'wednesday' as const,
            label: 'Onsdag',
            weekday: 3 as const,
            isOpen: true,
            openTime: '08:00',
            closeTime: '17:00',
          },
          {
            key: 'thursday' as const,
            label: 'Torsdag',
            weekday: 4 as const,
            isOpen: true,
            openTime: '08:00',
            closeTime: '17:00',
          },
          {
            key: 'friday' as const,
            label: 'Fredag',
            weekday: 5 as const,
            isOpen: true,
            openTime: '08:00',
            closeTime: '17:00',
          },
        ],
      },
    }
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale']),
    ...mapState(useTurnaroundTimeStore, ['allTATs']),

    activeWalkInTats(): TurnaroundTime[] {
      return this.allTATs.filter((x) => x.type === 'walk-in')
    },

    halfHourOptions(): { value: string; text: string }[] {
      const options = []
      for (let h = 0; h < 24; h++) {
        for (const m of [0, 30]) {
          const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
          options.push({ value, text: value })
        }
      }
      return options
    },

    walkInWindowRange(): { minTime: string; maxTime: string } | null {
      const activeDays = this.form.openingHours.filter((day) => day.isOpen)
      if (activeDays.length === 0) return null
      const latestOpen = activeDays.reduce(
        (latest, day) => (day.openTime > latest ? day.openTime : latest),
        activeDays[0]!.openTime
      )
      const earliestClose = activeDays.reduce(
        (earliest, day) => (day.closeTime < earliest ? day.closeTime : earliest),
        activeDays[0]!.closeTime
      )
      if (latestOpen >= earliestClose) return null
      return { minTime: latestOpen, maxTime: earliestClose }
    },

    walkInStartOptions(): { value: string; text: string }[] {
      const range = this.walkInWindowRange
      if (!range) return []
      return this.halfHourOptions.filter(
        (option) =>
          option.value >= range.minTime &&
          option.value <= range.maxTime &&
          (!this.form.walkInWindowEnd ||
            option.value < this.form.walkInWindowEnd)
      )
    },

    walkInEndOptions(): { value: string; text: string }[] {
      const range = this.walkInWindowRange
      if (!range) return []
      return this.halfHourOptions.filter(
        (option) =>
          option.value >= range.minTime &&
          option.value <= range.maxTime &&
          (!this.form.walkInWindowStart ||
            option.value > this.form.walkInWindowStart)
      )
    },

    walkInWindowForStore(): IDropPointLab['walkInWindow'] {
      if (
        this.form.walkInTatIDs.length === 0 ||
        !this.form.walkInWindowStart ||
        !this.form.walkInWindowEnd
      )
        return null
      const parseTime = (t: string) => ({
        hours: parseInt(t.split(':')[0] ?? '0'),
        minutes: parseInt(t.split(':')[1] ?? '0'),
      })
      return {
        start: parseTime(this.form.walkInWindowStart),
        end: parseTime(this.form.walkInWindowEnd),
      }
    },

    openingHoursForStore(): IDropPointLab['openingHours'] {
      const parseTime = (t: string) => ({
        hours: parseInt(t.split(':')[0] ?? '0'),
        minutes: parseInt(t.split(':')[1] ?? '0'),
      })
      const mon = this.form.openingHours[0]
      const tue = this.form.openingHours[1]
      const wed = this.form.openingHours[2]
      const thu = this.form.openingHours[3]
      const fri = this.form.openingHours[4]

      if (!mon || !tue || !wed || !thu || !fri)
        return {} as IDropPointLab['openingHours']

      return {
        monday: {
          weekday: 1,
          isOpen: mon.isOpen,
          open: parseTime(mon.openTime),
          close: parseTime(mon.closeTime),
        },
        tuesday: {
          weekday: 2,
          isOpen: tue.isOpen,
          open: parseTime(tue.openTime),
          close: parseTime(tue.closeTime),
        },
        wednesday: {
          weekday: 3,
          isOpen: wed.isOpen,
          open: parseTime(wed.openTime),
          close: parseTime(wed.closeTime),
        },
        thursday: {
          weekday: 4,
          isOpen: thu.isOpen,
          open: parseTime(thu.openTime),
          close: parseTime(thu.closeTime),
        },
        friday: {
          weekday: 5,
          isOpen: fri.isOpen,
          open: parseTime(fri.openTime),
          close: parseTime(fri.closeTime),
        },
      }
    },
  },

  watch: {
    walkInWindowRange() {
      this.sanitizeWalkInWindow()
    },
  },

  methods: {
    onMagicAddress(address: IMagicAddress) {
      this.form.address = address.addressLine
      this.form.postalCode = address.postalCode
      this.form.city = address.city
    },

    ...mapActions(useDropPointStore, ['createDropPoint']),
    ...mapActions(useModalStore, ['closeTop']),

    sanitizeWalkInWindow() {
      const range = this.walkInWindowRange
      const { walkInWindowStart: start, walkInWindowEnd: end } = this.form
      if (!start && !end) return
      const isValid =
        !!range &&
        !!start &&
        !!end &&
        start >= range.minTime &&
        start <= range.maxTime &&
        end >= range.minTime &&
        end <= range.maxTime &&
        start < end
      if (!isValid) {
        this.form.walkInWindowStart = ''
        this.form.walkInWindowEnd = ''
      }
    },

    async onCreate(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        await this.createDropPoint({
          name: this.form.name,
          address: this.form.address,
          postalCode: this.form.postalCode,
          city: this.form.city,
          placement: this.form.placement,
          phone: this.form.phone,
          openingHours: this.openingHoursForStore,
          walkInTatIDs: this.form.walkInTatIDs,
          walkInWindow: this.walkInWindowForStore,
          activeState: this.form.isActive ? 'active' : 'inactive',
          isSelfSendTarget: this.form.isSelfSendTarget,
        })
        useToast().success(this.$t('W: Droppested oprettet'))
        this.closeTop()
      } catch {
        useToast().error(this.$t('W: Kunne ikke oprette droppested'))
        this.errorText = this.$t('error.common.standard')
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>

